/*!
 * Magnuson Studio — Plaque Configurator engine v1.0.0
 * Plain browser JavaScript. No dependencies, no network libraries, no tracking.
 * Works standalone (index.html) and inside a Shopify theme section.
 * Exposes window.MagnusonPlaque.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (root) root.MagnusonPlaque = api;
  if (typeof document !== 'undefined' && root && !root.__MAGNUSON_NO_AUTOINIT__) api.autoInit();
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var VERSION = '1.0.0';
  var KEYS = ['sport', 'team', 'uniform', 'size', 'background', 'frame', 'paint'];
  var LABELS = { sport: 'Sport', team: 'Team', uniform: 'Uniform background', size: 'Size', background: 'Background', frame: 'Frame', paint: 'Paint color' };
  var DEFAULT_LAYOUT = { frameRatio: 0.075, artInsetRatio: 0.12, minScale: 0.62 };
  var CANVAS = { w: 800, h: 800, floorY: 706, maxW: 500, maxH: 590 };
  var WOOD = {
    walnut: { base: '#57392a', grain: '#2a190f', light: '#7a5640', seed: 7, lines: 16 },
    oak: { base: '#cdb084', grain: '#8c6a3e', light: '#e2cba3', seed: 13, lines: 11 }
  };
  var uidCounter = 0;

  /* ---------------- utilities ---------------- */
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function byId(list, id) {
    if (!list || id == null) return null;
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function safeColor(c, fallback) { return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(c || '')) ? c : fallback; }
  function safeUrl(u) {
    if (!u) return '';
    u = String(u).trim();
    return /^(https:\/\/|\/\/|\/(?!\/)|\.\/|assets\/)/i.test(u) ? u : '';
  }
  function luminance(hex) {
    hex = safeColor(hex, '#ffffff').slice(1);
    if (hex.length === 3) hex = hex.replace(/./g, '$&$&');
    var r = parseInt(hex.substr(0, 2), 16), g = parseInt(hex.substr(2, 2), 16), b = parseInt(hex.substr(4, 2), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }
  function seeded(seed) {
    var s = seed >>> 0;
    return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  }
  function n(v) { return Math.round(v * 100) / 100; }
  function sizeOrientation(size) {
    return size && (size.orientation === 'portrait' || size.orientation === 'landscape') ? size.orientation : null;
  }

  /* ---------------- configuration ---------------- */
  function normalizeConfig(raw) {
    var c = clone(raw || {});
    var errors = [], warnings = [];
    ['sports', 'teams', 'sizes', 'backgrounds', 'frames', 'paints'].forEach(function (k) {
      if (!Array.isArray(c[k])) { c[k] = []; errors.push('Configuration is missing "' + k + '".'); }
    });
    c.defaults = c.defaults || {};
    var bySort = function (a, b) { return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0); };
    c.sports = c.sports.filter(function (x) { return x.enabled !== false; }).sort(bySort);
    c.teams = c.teams.filter(function (x) { return x.enabled !== false && byId(c.sports, x.sport); }).sort(bySort);
    c.layout = Object.assign({}, DEFAULT_LAYOUT, c.layout || {});
    c.version = c.version || 'unversioned';
    c.teams.forEach(function (t) {
      t.colors = t.colors || {};
      t.artwork = t.artwork || {};
      if (!t.artwork.alt) t.artwork.alt = (t.label || t.id) + ' artwork';
      if (!(t.artwork.aspect > 0)) t.artwork.aspect = 1;
      if (!t.artwork.version) t.artwork.version = 'unversioned';
      if (!byId(c.sports, t.sport)) errors.push('Team "' + t.id + '" references unknown sport "' + t.sport + '".');
      if (!t.artwork.src && !Array.isArray(c.catalogWarnings)) warnings.push('Team "' + t.label + '" uses placeholder artwork.');
      if (t.uniforms != null) {
        var seenUniforms = {};
        if (!Array.isArray(t.uniforms) || !t.uniforms.length) {
          errors.push('Team "' + t.id + '" needs at least one valid uniform background.');
          t.uniforms = [];
        }
        t.uniforms = t.uniforms.filter(function (u) {
          if (!u || typeof u.id !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(u.id) || seenUniforms[u.id] || typeof u.label !== 'string' || !u.label.trim() || typeof u.colorName !== 'string' || !u.colorName.trim() || !safeColor(u.color, '')) {
            errors.push('Team "' + t.id + '" has an invalid or duplicate uniform background.');
            return false;
          }
          seenUniforms[u.id] = true;
          u.background = 'uniform-' + t.id + '-' + u.id;
          var bg = byId(c.backgrounds, u.background);
          if (!bg) { bg = { id: u.background }; c.backgrounds.push(bg); }
          bg.label = u.colorName;
          bg.color = u.color;
          return true;
        });
      }
    });
    c.sizes.forEach(function (s) {
      if (!(s.widthIn > 0 && s.heightIn > 0)) errors.push('Size "' + s.id + '" needs positive widthIn/heightIn.');
      if (s.approved !== true) warnings.push('Size "' + s.label + '" is an unapproved demo value.');
    });
    c.frames.forEach(function (f) {
      if (!f.type) f.type = f.id === 'painted' ? 'painted' : 'wood';
      if (f.type === 'wood' && !f.image) warnings.push('Frame "' + f.label + '" uses a drawn finish (no merchant wood image).');
    });
    c.teams = c.teams.filter(function (t) {
      if (t.uniforms && t.uniforms.length) return true;
      var bg = t.background && byId(c.backgrounds, t.background);
      if (!bg || !safeColor(bg.color, '')) { warnings.push('Team "' + (t.label || t.id) + '" is hidden: it has no valid background preset.'); return false; }
      return true;
    });
    c.sports = c.sports.filter(function (sp) {
      if (c.teams.some(function (t) { return t.sport === sp.id; })) return true;
      warnings.push('Sport "' + (sp.label || sp.id) + '" is hidden: it has no valid enabled teams.');
      return false;
    });
    if (c.frames.some(function (f) { return f.type === 'painted' && !f.color; }) && !c.paints.length) errors.push('Painted frame exists but no paints are configured.');
    Object.defineProperty(c, '_errors', { value: errors, enumerable: false });
    if (Array.isArray(c.catalogWarnings)) warnings = c.catalogWarnings.concat(warnings);
    Object.defineProperty(c, '_warnings', { value: warnings, enumerable: false });
    return c;
  }

  function teamsForSport(cfg, sportId) {
    return cfg.teams.filter(function (t) { return t.sport === sportId; });
  }

  function isPainted(cfg, frameId) {
    // True only when the customer must pick a paint (painted frame without a fixed colour).
    var f = byId(cfg.frames, frameId);
    return !!(f && f.type === 'painted' && !f.color);
  }
  function uniformsForTeam(cfg, teamId) { return (byId(cfg.teams, teamId) || {}).uniforms || []; }
  function teamBackground(cfg, team, uniformId) {
    var uniform = team && byId(team.uniforms, uniformId);
    if (uniform) return uniform.background;
    return team && team.background && byId(cfg.backgrounds, team.background) ? team.background : null;
  }
  function hasFatalErrors(cfg) { return !cfg || !Array.isArray(cfg._errors) || cfg._errors.length > 0; }
  // Serialize data for an inline <script type="application/json">: escapes <, >, & and U+2028/9 so configured
  // text can never close the element (e.g. "</script>") or open a comment. Output is still valid JSON.
  function jsonForHtml(value) {
    return JSON.stringify(value, null, 2).replace(/[<>&\u2028\u2029]/g, function (ch) {
      return '\\u' + ('0000' + ch.charCodeAt(0).toString(16)).slice(-4);
    });
  }

  /* ---------------- state ---------------- */
  function revalidate(cfg, s) {
    var x = {};
    KEYS.forEach(function (k) { x[k] = s && s[k] != null ? s[k] : null; });
    if (!byId(cfg.sports, x.sport)) x.sport = null;
    var team = byId(cfg.teams, x.team);
    if (!team || team.sport !== x.sport) x.team = null;
    if (cfg.sizes.length === 1) x.size = cfg.sizes[0].id;
    if (!byId(cfg.sizes, x.size)) x.size = null;
    var uniforms = uniformsForTeam(cfg, x.team);
    if (uniforms.length) {
      if (!byId(uniforms, x.uniform)) x.uniform = (byId(uniforms, 'home') || uniforms[0]).id;
    } else if (x.team) x.uniform = null;
    x.background = teamBackground(cfg, byId(cfg.teams, x.team), x.uniform);
    if (!byId(cfg.backgrounds, x.background)) x.background = null;
    if (!byId(cfg.frames, x.frame)) x.frame = null;
    if (!isPainted(cfg, x.frame) || !byId(cfg.paints, x.paint)) x.paint = null;
    return x;
  }

  function initialState(cfg) { return revalidate(cfg, cfg.defaults || {}); }

  function setChoice(cfg, s, key, value) {
    if (KEYS.indexOf(key) < 0) return revalidate(cfg, s);
    var next = Object.assign({}, s);
    next[key] = value;
    if (key === 'frame' && isPainted(cfg, value) && !next.paint) next.paint = (cfg.defaults && cfg.defaults.paint) || null;
    return revalidate(cfg, next);
  }

  function missingChoices(cfg, s) {
    var m = [];
    ['sport', 'team', 'size', 'background', 'frame'].forEach(function (k) { if (!s[k]) m.push(k); });
    if (isPainted(cfg, s.frame) && !s.paint) m.push('paint');
    return m;
  }

  /* ---------------- geometry ---------------- */
  function fitContain(box, aspect) {
    var w, h;
    if (box.w / box.h > aspect) { h = box.h; w = h * aspect; } else { w = box.w; h = w / aspect; }
    return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w: w, h: h };
  }

  function geometry(cfg, s) {
    if (hasFatalErrors(cfg)) throw new Error('MagnusonPlaque.geometry: configuration has fatal errors');
    var size = byId(cfg.sizes, s.size) || cfg.sizes[0] || { widthIn: 12, heightIn: 18 };
    var unit = Infinity;
    (cfg.sizes.length ? cfg.sizes : [size]).forEach(function (z) {
      unit = Math.min(unit, CANVAS.maxW / z.widthIn, CANVAS.maxH / z.heightIn);
    });
    var own = Math.min(CANVAS.maxW / size.widthIn, CANVAS.maxH / size.heightIn);
    var scale = Math.max(unit, own * cfg.layout.minScale);
    var w = size.widthIn * scale, h = size.heightIn * scale;
    var plaque = { x: (CANVAS.w - w) / 2, y: CANVAS.floorY - h, w: w, h: h };
    var t = Math.min(w, h) * cfg.layout.frameRatio;
    var inner = { x: plaque.x + t, y: plaque.y + t, w: w - 2 * t, h: h - 2 * t };
    var team = byId(cfg.teams, s.team);
    var ratio = team && team.layout && team.layout.artInsetRatio > 0 && team.layout.artInsetRatio < 0.45 ? team.layout.artInsetRatio : cfg.layout.artInsetRatio;
    var inset = Math.min(inner.w, inner.h) * ratio;
    var artBox = { x: inner.x + inset, y: inner.y + inset, w: inner.w - 2 * inset, h: inner.h - 2 * inset };
    var aspect = team ? team.artwork.aspect : 1;
    return { canvas: CANVAS, size: size, plaque: plaque, frame: t, inner: inner, artBox: artBox, art: fitContain(artBox, aspect), aspect: aspect };
  }

  /* ---------------- variants & order ---------------- */
  function resolveVariant(cfg, s, ctx) {
    if (hasFatalErrors(cfg)) return { status: 'invalid-config', errors: (cfg && cfg._errors) || [] };
    var missing = missingChoices(cfg, s);
    if (missing.length) return { status: 'incomplete', missing: missing };
    var map = (ctx && ctx.variantMap) || [];
    var variants = (ctx && ctx.variants) || [];
    var painted = isPainted(cfg, s.frame);
    var entry = null;
    for (var i = 0; i < map.length; i++) {
      var e = map[i];
      if (e.size === s.size && e.frame === s.frame && String(e.paint || '') === (painted ? String(s.paint) : '')) { entry = e; break; }
    }
    if (!entry || !entry.variant) return { status: 'unmapped' };
    var v = null;
    for (var j = 0; j < variants.length; j++) if (String(variants[j].id) === String(entry.variant).trim()) { v = variants[j]; break; }
    if (!v) return { status: 'unmapped', reason: 'variant-not-in-product' };
    if (!v.available) return { status: 'unavailable', variant: v };
    return { status: 'ready', variant: v };
  }

  // Production readiness is stricter than demo variant resolution. All purchase paths share this gate.
  function resolvePurchase(cfg, s, ctx, artwork) {
    if (hasFatalErrors(cfg)) return { status: 'invalid-config', errors: (cfg && cfg._errors) || [] };
    var missing = missingChoices(cfg, s);
    if (missing.length) return { status: 'incomplete', missing: missing };
    var size = byId(cfg.sizes, s.size);
    if (!size || size.approved !== true) return { status: 'unapproved-size' };
    var team = byId(cfg.teams, s.team);
    var url = team && team.artwork && safeUrl(team.artwork.src);
    if (!url) return { status: 'missing-artwork' };
    if (!artwork || artwork.url !== url || artwork.status === 'loading' || artwork.status === 'missing') return { status: 'artwork-loading' };
    if (artwork.status !== 'loaded') return { status: 'artwork-failed' };
    return resolveVariant(cfg, s, ctx);
  }

  function createArtworkLoader(opts) {
    opts = opts || {};
    var state = { url: '', status: 'missing' }, revision = 0, image = null, disposed = false;
    var makeImage = opts.createImage || function () { return new Image(); };
    function snapshot() { return { url: state.url, status: state.status }; }
    function select(value, retry) {
      if (disposed) return snapshot();
      var url = safeUrl(value);
      if (!retry && url === state.url) return snapshot();
      var ticket = ++revision;
      if (image) { image.onload = null; image.onerror = null; }
      image = null;
      state = { url: url, status: url ? 'loading' : 'missing' };
      if (!url) return snapshot();
      function finish(status) {
        if (disposed || ticket !== revision) return;
        state.status = status;
        if (image) { image.onload = null; image.onerror = null; }
        if (opts.onChange) opts.onChange(snapshot());
      }
      try {
        var pending = makeImage();
        image = pending;
        pending.onload = function () { finish(pending.naturalWidth > 0 && pending.naturalHeight > 0 ? 'loaded' : 'failed'); };
        pending.onerror = function () { finish('failed'); };
        pending.src = url;
      } catch (e) { finish('failed'); }
      return snapshot();
    }
    return {
      select: select,
      getState: snapshot,
      retry: function () { return select(state.url, true); },
      destroy: function () { disposed = true; revision++; if (image) { image.onload = null; image.onerror = null; } image = null; }
    };
  }

  function orderProperties(cfg, s) {
    var team = byId(cfg.teams, s.team);
    var size = byId(cfg.sizes, s.size);
    // Size is canonical: never serialize a stale, independently supplied state.orientation.
    var orientation = sizeOrientation(size);
    var uniform = byId(uniformsForTeam(cfg, s.team), s.uniform);
    var props = {
      'Sport': (byId(cfg.sports, s.sport) || {}).label,
      'Team': team && team.label,
      'Uniform background': uniform && uniform.label,
      'Size': size && size.label,
      'Orientation': orientation ? orientation.charAt(0).toUpperCase() + orientation.slice(1) : null,
      'Frame': (byId(cfg.frames, s.frame) || {}).label,
      // Manufacturing-only data uses a leading underscore: Shopify themes hide these in cart/checkout.
      '_Background': (byId(cfg.backgrounds, s.background) || {}).label,
      '_Artwork version': team && team.artwork.version
    };
    if (isPainted(cfg, s.frame)) props['Paint color'] = (byId(cfg.paints, s.paint) || {}).label;
    var out = {};
    Object.keys(props).forEach(function (k) { if (props[k]) out[k] = String(props[k]); });
    var selection = { v: cfg.version, sport: s.sport, team: s.team, size: s.size, frame: s.frame, paint: isPainted(cfg, s.frame) ? s.paint : null, background: s.background, artwork: team ? team.artwork.version : null };
    if (orientation) selection.orientation = orientation;
    if (uniform) {
      selection.uniform = uniform.id;
      selection.backgroundColor = uniform.color;
    }
    out['_Plaque config'] = JSON.stringify(selection);
    return out;
  }

  function summaryText(cfg, s) {
    var p = orderProperties(cfg, s);
    return Object.keys(p).filter(function (k) { return k.charAt(0) !== '_'; }).map(function (k) { return k + ': ' + p[k]; }).join('\n');
  }

  function routesRoot(r) { r = r || '/'; return r.charAt(r.length - 1) === '/' ? r : r + '/'; }

  function createCartSubmitter(opts) {
    var busy = false;
    return {
      isBusy: function () { return busy; },
      submit: function (payload) {
        if (busy) return Promise.resolve({ status: 'busy' });
        if (!payload || !payload.variantId) return Promise.resolve({ status: 'error', message: 'No product option is selected.' });
        busy = true;
        var url = routesRoot(opts.routesRoot) + 'cart/add.js';
        return Promise.resolve()
          .then(function () {
            return opts.fetch(url, {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items: [{ id: Number(payload.variantId), quantity: 1, properties: payload.properties || {} }] })
            });
          })
          .then(function (res) {
            return Promise.resolve(res.json ? res.json() : {}).catch(function () { return {}; }).then(function (body) {
              if (res.ok) return { status: 'ok', body: body };
              return { status: 'error', code: res.status, message: (body && (body.description || body.message)) || ('The cart could not be updated (' + res.status + ').') };
            });
          })
          .catch(function () { return { status: 'error', message: 'Network problem — the cart could not be reached.' }; })
          .then(function (r) { busy = false; return r; });
      }
    };
  }

  /* ---------------- SVG rendering ---------------- */
  function woodPattern(id, wood, img, rot) {
    if (img) {
      return '<pattern id="' + id + '" patternUnits="userSpaceOnUse" width="260" height="260" patternTransform="rotate(' + rot + ')">' +
        '<image href="' + esc(img) + '" width="260" height="260" preserveAspectRatio="xMidYMid slice"/></pattern>';
    }
    var rnd = seeded(wood.seed + rot), lines = '', H = 44;
    for (var i = 0; i < wood.lines; i++) {
      var y = (i + 0.5) * (H / wood.lines) + (rnd() - 0.5) * 1.4, a = 0.6 + rnd() * 2.2;
      lines += '<path d="M0 ' + n(y) + ' C 80 ' + n(y + a) + ', 160 ' + n(y - a) + ', 240 ' + n(y + a * 0.4) + ' S 330 ' + n(y) + ' 360 ' + n(y) + '" stroke="' + wood.grain +
        '" stroke-opacity="' + n(0.18 + rnd() * 0.42) + '" stroke-width="' + n(0.35 + rnd() * 1.2) + '" fill="none"/>';
    }
    return '<pattern id="' + id + '" patternUnits="userSpaceOnUse" width="360" height="' + H + '" patternTransform="rotate(' + rot + ')">' +
      '<rect width="360" height="' + H + '" fill="' + wood.base + '"/><rect width="360" height="' + (H / 2) + '" fill="' + wood.light + '" opacity="0.12"/>' + lines + '</pattern>';
  }

  function placeholderArt(team, sportLabel, A) {
    var p = safeColor(team.colors.primary, '#3d4a3a'), s2 = safeColor(team.colors.secondary, '#f3efe6');
    var label = String(team.shortLabel || team.label || '').slice(0, 12);
    var fs = label.length <= 4 ? 26 : label.length <= 6 ? 19 : label.length <= 9 ? 13.5 : 11;
    return '<svg x="' + n(A.x) + '" y="' + n(A.y) + '" width="' + n(A.w) + '" height="' + n(A.h) + '" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">' +
      '<title>' + esc(team.artwork.alt) + '</title>' +
      '<circle cx="50" cy="50" r="47" fill="' + p + '"/>' +
      '<circle cx="50" cy="50" r="41.5" fill="none" stroke="' + s2 + '" stroke-width="1"/>' +
      '<text x="50" y="31" text-anchor="middle" font-family="Georgia, serif" font-size="5.6" letter-spacing="1.6" fill="' + s2 + '">' + esc(String(sportLabel).toUpperCase()) + '</text>' +
      '<text x="50" y="' + n(50 + fs * 0.34) + '" text-anchor="middle" font-family="Georgia, serif" font-weight="700" font-size="' + fs + '" fill="' + s2 + '">' + esc(label) + '</text>' +
      '<line x1="34" y1="66" x2="66" y2="66" stroke="' + s2 + '" stroke-width="0.6" opacity="0.7"/>' +
      '<text x="50" y="74" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="4.2" letter-spacing="1.1" fill="' + s2 + '" opacity="0.85">SAMPLE ARTWORK</text></svg>';
  }

  function buildSvg(cfg, s, uid) {
    if (hasFatalErrors(cfg)) return '';
    var g = geometry(cfg, s), P = g.plaque, I = g.inner, A = g.art, F = CANVAS.floorY;
    var frame = byId(cfg.frames, s.frame) || { id: 'none', type: 'wood', finish: 'walnut' };
    var bg = byId(cfg.backgrounds, s.background);
    var bgColor = safeColor(bg && bg.color, '#f1ede5');
    var team = byId(cfg.teams, s.team);
    var sport = byId(cfg.sports, s.sport);
    var defs = '', railH, railV;
    if (frame.type === 'painted') {
      var paint = byId(cfg.paints, s.paint);
      railH = railV = safeColor(frame.color || (paint && paint.color), '#d8d3c9');
    } else {
      var wood = WOOD[frame.finish] || (frame.id === 'white-oak' ? WOOD.oak : WOOD.walnut);
      var img = safeUrl(frame.image);
      defs += woodPattern(uid + '-gh', wood, img, 0) + woodPattern(uid + '-gv', wood, img, 90);
      railH = 'url(#' + uid + '-gh)'; railV = 'url(#' + uid + '-gv)';
    }
    defs +=
      '<radialGradient id="' + uid + '-cs" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#1d1814" stop-opacity="0.42"/><stop offset="0.3" stop-color="#1d1814" stop-opacity="0.12"/><stop offset="1" stop-color="#1d1814" stop-opacity="0"/></radialGradient>' +
      '<filter id="' + uid + '-b1" x="-10%" y="-300%" width="120%" height="700%"><feGaussianBlur stdDeviation="1.1"/></filter>' +
      '<filter id="' + uid + '-b3" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="3"/></filter>' +
      '<filter id="' + uid + '-b9" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="9"/></filter>' +
      '<filter id="' + uid + '-relief" x="-15%" y="-15%" width="130%" height="135%"><feDropShadow dx="0" dy="1.8" stdDeviation="1.6" flood-color="#1d1814" flood-opacity="0.38"/></filter>' +
      '<clipPath id="' + uid + '-inner"><rect x="' + n(I.x) + '" y="' + n(I.y) + '" width="' + n(I.w) + '" height="' + n(I.h) + '"/></clipPath>';

    var x = P.x, y = P.y, w = P.w, h = P.h, ix = I.x, iy = I.y, iw = I.w, ih = I.h;
    var top = 'M' + n(x) + ' ' + n(y) + 'L' + n(x + w) + ' ' + n(y) + 'L' + n(ix + iw) + ' ' + n(iy) + 'L' + n(ix) + ' ' + n(iy) + 'Z';
    var right = 'M' + n(x + w) + ' ' + n(y) + 'L' + n(x + w) + ' ' + n(y + h) + 'L' + n(ix + iw) + ' ' + n(iy + ih) + 'L' + n(ix + iw) + ' ' + n(iy) + 'Z';
    var bottom = 'M' + n(x) + ' ' + n(y + h) + 'L' + n(x + w) + ' ' + n(y + h) + 'L' + n(ix + iw) + ' ' + n(iy + ih) + 'L' + n(ix) + ' ' + n(iy + ih) + 'Z';
    var left = 'M' + n(x) + ' ' + n(y) + 'L' + n(ix) + ' ' + n(iy) + 'L' + n(ix) + ' ' + n(iy + ih) + 'L' + n(x) + ' ' + n(y + h) + 'Z';

    var body = '';
    // soft wall shadow + thin contact shadow fading quickly to white
    body += '<rect x="' + n(x + 6) + '" y="' + n(y + 10) + '" width="' + n(w - 6) + '" height="' + n(h - 12) + '" fill="#1d1814" opacity="0.10" filter="url(#' + uid + '-b9)"/>';
    body += '<ellipse cx="' + n(x + w / 2) + '" cy="' + n(F + 2) + '" rx="' + n(w * 0.62) + '" ry="9" fill="url(#' + uid + '-cs)"/>';
    body += '<ellipse cx="' + n(x + w / 2) + '" cy="' + n(F + 0.6) + '" rx="' + n(w * 0.5) + '" ry="1.5" fill="#1d1814" opacity="0.6" filter="url(#' + uid + '-b1)"/>';
    // frame rails
    body += '<path d="' + top + '" fill="' + railH + '"/><path d="' + bottom + '" fill="' + railH + '"/>';
    body += '<path d="' + left + '" fill="' + railV + '"/><path d="' + right + '" fill="' + railV + '"/>';
    // relief shading
    body += '<path d="' + top + '" fill="#ffffff" opacity="0.16"/><path d="' + left + '" fill="#ffffff" opacity="0.07"/>';
    body += '<path d="' + right + '" fill="#000000" opacity="0.10"/><path d="' + bottom + '" fill="#000000" opacity="0.20"/>';
    body += '<g stroke="#000" stroke-opacity="0.22" stroke-width="0.7">' +
      '<line x1="' + n(x) + '" y1="' + n(y) + '" x2="' + n(ix) + '" y2="' + n(iy) + '"/><line x1="' + n(x + w) + '" y1="' + n(y) + '" x2="' + n(ix + iw) + '" y2="' + n(iy) + '"/>' +
      '<line x1="' + n(x) + '" y1="' + n(y + h) + '" x2="' + n(ix) + '" y2="' + n(iy + ih) + '"/><line x1="' + n(x + w) + '" y1="' + n(y + h) + '" x2="' + n(ix + iw) + '" y2="' + n(iy + ih) + '"/></g>';
    // backing
    body += '<rect x="' + n(ix) + '" y="' + n(iy) + '" width="' + n(iw) + '" height="' + n(ih) + '" fill="' + bgColor + '"/>';
    body += '<g clip-path="url(#' + uid + '-inner)"><rect x="' + n(ix) + '" y="' + n(iy) + '" width="' + n(iw) + '" height="' + n(ih) + '" fill="none" stroke="#000" stroke-opacity="0.34" stroke-width="9" filter="url(#' + uid + '-b3)"/></g>';
    // artwork
    if (team) {
      body += '<g clip-path="url(#' + uid + '-inner)"><g filter="url(#' + uid + '-relief)">';
      var src = safeUrl(team.artwork.src);
      if (src) {
        body += '<image href="' + esc(src) + '" x="' + n(A.x) + '" y="' + n(A.y) + '" width="' + n(A.w) + '" height="' + n(A.h) + '" preserveAspectRatio="xMidYMid meet"><title>' + esc(team.artwork.alt) + '</title></image>';
      } else {
        body += placeholderArt(team, sport ? sport.label : '', A);
      }
      body += '</g></g>';
    } else {
      var ink = luminance(bgColor) > 0.55 ? '#5a534b' : '#efe9dd';
      body += '<text x="' + n(ix + iw / 2) + '" y="' + n(iy + ih / 2) + '" text-anchor="middle" font-family="Georgia, serif" font-size="16" fill="' + ink + '" opacity="0.8">Select a team</text>';
    }
    // outer edge
    body += '<rect x="' + n(x + 0.5) + '" y="' + n(y + 0.5) + '" width="' + n(w - 1) + '" height="' + n(h - 1) + '" fill="none" stroke="#1d1814" stroke-opacity="0.35"/>';
    body += '<rect x="' + n(ix - 0.5) + '" y="' + n(iy - 0.5) + '" width="' + n(iw + 1) + '" height="' + n(ih + 1) + '" fill="none" stroke="#1d1814" stroke-opacity="0.3"/>';

    var desc = 'Mockup: ' + summaryText(cfg, s).replace(/\n/g, ', ');
    return '<svg class="mpc-svg" viewBox="0 0 ' + CANVAS.w + ' ' + CANVAS.h + '" role="img" aria-labelledby="' + uid + '-t ' + uid + '-d" xmlns="http://www.w3.org/2000/svg">' +
      '<title id="' + uid + '-t">Plaque preview</title><desc id="' + uid + '-d">' + esc(desc) + '</desc><defs>' + defs + '</defs>' + body + '</svg>';
  }

  /* ---------------- merchant catalog (Shopify metaobjects) ---------------- */
  // records = { sports:[{handle,name,enabled,sort_order}], teams:[{handle,name,sport,enabled,sort_order,background_color,artwork_version,logo:{src,alt,aspect}|null}] }
  // Mirrors the Liquid filtering so disabled / unpublished / disallowed records can never become choices.
  function catalogFromRecords(records, opts) {
    records = records || {};
    opts = opts || {};
    var allowed = Array.isArray(opts.allowedSports) && opts.allowedSports.length ? opts.allowedSports : null;
    var warnings = [];
    var sort = function (a, b) { return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || String(a.name).localeCompare(String(b.name)); };
    var seen = {};
    var sports = (records.sports || []).filter(function (x) {
      if (!x || !x.handle || x.enabled !== true) return false;
      if (allowed && allowed.indexOf(x.handle) < 0) return false;
      if (seen[x.handle]) return false;
      return (seen[x.handle] = true);
    }).sort(sort);
    var teamSeen = {};
    var teams = (records.teams || []).filter(function (t) {
      if (!t || !t.handle || t.enabled !== true || teamSeen[t.handle]) return false;
      if (!seen[t.sport]) return false;
      if (!safeColor(t.background_color, '')) { warnings.push('Team "' + t.name + '" is hidden: background color is missing or invalid.'); return false; }
      return (teamSeen[t.handle] = true);
    }).sort(sort);
    var withTeams = {};
    teams.forEach(function (t) { withTeams[t.sport] = true; });
    sports = sports.filter(function (x) {
      if (!withTeams[x.handle]) { warnings.push('Sport "' + x.name + '" is enabled but hidden: it has no enabled teams yet.'); return false; }
      return true;
    });
    var backgrounds = [];
    var outTeams = teams.map(function (t) {
      var bg = t.background_color;
      var contrast = luminance(bg) > 0.55 ? '#2b2723' : '#f3efe6';
      backgrounds.push({ id: 'bg-' + t.handle, label: t.name + ' preset (' + String(bg).toUpperCase() + ')', color: bg });
      var logo = t.logo && safeUrl(t.logo.src) ? t.logo : null;
      if (!logo) warnings.push('Team "' + t.name + '" has no logo yet — placeholder artwork is shown.');
      return {
        id: t.handle, sport: t.sport, label: t.name, shortLabel: String(t.name).toUpperCase(),
        background: 'bg-' + t.handle,
        colors: t.colors || { primary: contrast, secondary: bg },
        artwork: { src: logo ? logo.src : '', alt: (logo && logo.alt) || (t.name + (logo ? ' logo' : ' placeholder artwork')), aspect: logo && logo.aspect > 0 ? Number(logo.aspect) : 1, version: t.artwork_version || 'unversioned' }
      };
    });
    return { sports: sports.map(function (x) { return { id: x.handle, label: x.name }; }), teams: outTeams, backgrounds: backgrounds, warnings: warnings };
  }

  function applyCatalog(rawConfig, catalog) {
    var c = clone(rawConfig || {});
    c.sports = catalog.sports;
    c.teams = catalog.teams;
    c.backgrounds = catalog.backgrounds;
    c.defaults = Object.assign({}, c.defaults || {});
    if (!byId(c.sports, c.defaults.sport)) c.defaults.sport = c.sports[0] ? c.sports[0].id : null;
    var t = byId(c.teams, c.defaults.team);
    if (!t || t.sport !== c.defaults.sport) {
      var first = c.teams.filter(function (x) { return x.sport === c.defaults.sport; })[0];
      c.defaults.team = first ? first.id : null;
    }
    c.catalogWarnings = catalog.warnings || [];
    return c;
  }

  function mergeRecordPages(pages) {
    var out = { sports: [], teams: [] }, sportSeen = {}, teamSeen = {};
    pages.forEach(function (p) {
      (p.sports || []).forEach(function (x) { if (!sportSeen[x.handle]) { sportSeen[x.handle] = 1; out.sports.push(x); } });
      (p.teams || []).forEach(function (x) { if (!teamSeen[x.handle]) { teamSeen[x.handle] = 1; out.teams.push(x); } });
    });
    return out;
  }

  /* ---------------- UI instance ---------------- */
  function optionHtml(uid, key, item, swatch) {
    var sw = swatch ? '<span class="mpc-swatch" style="--mpc-sw:' + safeColor(swatch, '#cccccc') + '" aria-hidden="true"></span>' : '';
    return '<label class="mpc-opt' + (swatch ? ' mpc-opt--swatch' : '') + '">' +
      '<input class="mpc-opt__input" type="radio" name="' + uid + '-' + key + '" data-key="' + key + '" value="' + esc(item.id) + '">' +
      '<span class="mpc-opt__body">' + sw + '<span class="mpc-opt__label">' + esc(item.label) + '</span></span></label>';
  }

  function frameSwatch(f) {
    if (f.type === 'painted') return f.color || null;
    return (WOOD[f.finish] || (f.id === 'white-oak' ? WOOD.oak : WOOD.walnut)).base;
  }

  function mount(el, options) {
    if (!el) throw new Error('MagnusonPlaque.mount: element is required');
    if (el.__magnusonPlaque) el.__magnusonPlaque.destroy();
    options = options || {};
    var cfg = normalizeConfig(options.config);
    var uid = 'mpc' + (++uidCounter) + '-' + Math.random().toString(36).slice(2, 7);
    var mode = options.mode || 'preview';
    var ctx = null;
    if (!cfg.teams.length) mode = mode === 'shopify' ? 'shopify-empty' : mode;
    if (mode === 'shopify' && options.product) ctx = { variants: options.product.variants || [], variantMap: options.variantMap || [] };
    if (mode === 'demo') ctx = options.fixtures || { variants: [], variantMap: [] };
    var form = options.form || null;
    var fetchImpl = options.fetch || (typeof fetch !== 'undefined' ? fetch.bind(typeof window !== 'undefined' ? window : null) : null);
    var submitter = createCartSubmitter({ fetch: fetchImpl, routesRoot: options.routesRoot });
    var state = initialState(cfg);
    var lastError = null;
    var destroyed = false;
    var artworkLoader = mode === 'shopify' ? createArtworkLoader({
      createImage: options.createImage,
      onChange: function () { if (!destroyed) update(); }
    }) : null;

    el.classList.add('mpc-root');
    if (options.buttonClass) el.classList.add('mpc--theme-buttons');
    var heading = options.heading != null ? String(options.heading) : 'Sports plaque';
    var eyebrow = options.eyebrow != null ? String(options.eyebrow) : 'Magnuson Studio';
    var btnPrimary = 'mpc-btn mpc-btn--primary' + (options.buttonClass ? ' ' + esc(options.buttonClass) : '');
    var btnSecondary = 'mpc-btn mpc-btn--ghost' + (options.buttonSecondaryClass ? ' ' + esc(options.buttonSecondaryClass) : '');
    var purchaseLabel = mode === 'shopify-empty' ? 'No teams are available yet' : mode === 'shopify' ? (ctx ? '' : 'Pricing not configured') : mode === 'demo' ? 'Demo fixtures — not real prices' : 'Preview only — pricing not configured';
    el.innerHTML =
      '<div class="mpc">' +
      '<div class="mpc__stage"><div class="mpc__studio" data-ref="stage"></div>' +
      '<p class="mpc__caption"><span data-ref="dims"></span> · Visual mockup, not a photograph of a finished piece.</p></div>' +
      '<div class="mpc__panel">' +
      '<header class="mpc__head">' + (eyebrow ? '<p class="mpc__eyebrow">' + esc(eyebrow) + '</p>' : '') + (heading ? '<h2 class="mpc__title">' + esc(heading) + '</h2>' : '') + '</header>' +
      group('sport', 'Sport', cfg.sports.map(function (x) { return optionHtml(uid, 'sport', x); }).join('')) +
      group('team', 'Team', '') +
      (cfg.teams.some(function (t) { return t.uniforms && t.uniforms.length; }) ? group('uniform', 'Uniform background', '', true) : '') +
      (cfg.sizes.length <= 1 ? '' : group('size', cfg.sizes.every(function (x) { return !!sizeOrientation(x); }) ? 'Orientation' : 'Size', cfg.sizes.map(function (x) { return optionHtml(uid, 'size', x); }).join(''))) +
      group('frame', 'Frame', cfg.frames.map(function (x) { return optionHtml(uid, 'frame', x, frameSwatch(x)); }).join('')) +
      (cfg.frames.some(function (f) { return f.type === 'painted' && !f.color; }) ? group('paint', 'Paint color', cfg.paints.map(function (x) { return optionHtml(uid, 'paint', x, x.color); }).join(''), true) : '') +
      '<section class="mpc__summary" aria-labelledby="' + uid + '-sum"><h3 class="mpc__h3" id="' + uid + '-sum">Your selection</h3><dl class="mpc__dl" data-ref="summary"></dl></section>' +
      '<div class="mpc__buy">' +
      '<p class="mpc__price" data-ref="price" role="status" aria-live="polite">' + esc(purchaseLabel) + '</p>' +
      '<div class="mpc__actions">' +
      (mode === 'shopify' || mode === 'shopify-empty'
        ? '<button type="button" class="' + btnPrimary + '" data-action="add">Add to cart</button>'
        : mode === 'demo'
          ? '<button type="button" class="' + btnPrimary + '" data-action="simulate">Check demo variant</button>'
          : '<button type="button" class="' + btnPrimary + '" data-action="summary">Copy selection summary</button>') +
      '<button type="button" class="' + btnSecondary + '" data-action="reset">Reset</button></div>' +
      (artworkLoader ? '<div class="mpc__recover" data-ref="artwork-recover" hidden><button type="button" class="' + btnSecondary + '" data-action="artwork-retry">Retry artwork</button></div>' : '') +
      '<div class="mpc__status" role="status" aria-live="polite" data-ref="status"></div>' +
      '<div class="mpc__recover" data-ref="recover" hidden>' +
      '<button type="button" class="' + btnSecondary + '" data-action="retry">Try again</button>' +
      (form ? '<button type="button" class="mpc-btn mpc-btn--link" data-action="fallback">Add using the standard cart page</button>' : '') +
      '</div></div></div></div>';

    function group(key, legend, inner, swatches) {
      return '<fieldset class="mpc__group" data-group="' + key + '"><legend class="mpc__legend">' + legend +
        ' <span class="mpc__chosen" data-chosen="' + key + '"></span></legend><div class="mpc__opts' + (swatches ? ' mpc__opts--swatches' : '') + '" data-opts="' + key + '">' + inner + '</div>' +
        (key === 'uniform' ? '<p class="mpc__hint mpc__uniform-note" data-ref="uniform-note" hidden></p>' : '') + '</fieldset>';
    }
    function ref(name) { return el.querySelector('[data-ref="' + name + '"]'); }

    function renderTeams() {
      var box = el.querySelector('[data-opts="team"]');
      var list = state.sport ? teamsForSport(cfg, state.sport) : [];
      box.innerHTML = list.length ? list.map(function (t) { return optionHtml(uid, 'team', t, t.colors.primary); }).join('')
        : '<p class="mpc__hint">' + (state.sport ? 'No teams configured for this sport.' : 'Choose a sport first.') + '</p>';
    }

    var renderedUniformTeam;
    function renderUniforms() {
      var group = el.querySelector('[data-group="uniform"]');
      if (!group) return;
      var team = byId(cfg.teams, state.team);
      var uniforms = uniformsForTeam(cfg, state.team);
      group.hidden = !!team && !uniforms.length;
      if (renderedUniformTeam !== state.team) {
        renderedUniformTeam = state.team;
        el.querySelector('[data-opts="uniform"]').innerHTML = uniforms.length
          ? uniforms.map(function (u) { return optionHtml(uid, 'uniform', u, u.color); }).join('')
          : '<p class="mpc__hint">Choose a team first.</p>';
      }
      var uniform = byId(uniforms, state.uniform);
      var note = ref('uniform-note');
      note.textContent = [uniforms.length && cfg.uniformNote, team && team.uniformNote, uniform && uniform.note].filter(Boolean).join(' ');
      note.hidden = !note.textContent;
    }

    function setStatus(msg, tone) {
      var st = ref('status');
      st.textContent = msg || '';
      st.className = 'mpc__status' + (tone ? ' mpc__status--' + tone : '');
    }

    function syncForm(res) {
      if (!form) return;
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput) { idInput = document.createElement('input'); idInput.type = 'hidden'; idInput.name = 'id'; form.appendChild(idInput); }
      idInput.value = res.status === 'ready' ? String(res.variant.id) : '';
      Array.prototype.slice.call(form.querySelectorAll('[data-mpc-prop]')).forEach(function (i) { i.parentNode.removeChild(i); });
      if (res.status !== 'ready') return;
      var props = orderProperties(cfg, state);
      Object.keys(props).forEach(function (k) {
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = props[k]; i.setAttribute('data-mpc-prop', '');
        form.appendChild(i);
      });
    }

    function current() {
      if (mode === 'shopify') return resolvePurchase(cfg, state, ctx, artworkLoader.getState());
      return ctx ? resolveVariant(cfg, state, ctx) : { status: 'unpriced', missing: missingChoices(cfg, state) };
    }

    function update() {
      renderUniforms();
      if (artworkLoader) {
        var selectedTeam = byId(cfg.teams, state.team);
        artworkLoader.select(selectedTeam && selectedTeam.artwork.src);
      }
      Array.prototype.slice.call(el.querySelectorAll('.mpc-opt__input')).forEach(function (i) { i.checked = state[i.getAttribute('data-key')] === i.value; });
      var pg = el.querySelector('[data-group="paint"]');
      if (pg) pg.hidden = !isPainted(cfg, state.frame);
      KEYS.forEach(function (k) {
        var c = el.querySelector('[data-chosen="' + k + '"]');
        var list = { sport: cfg.sports, team: cfg.teams, uniform: uniformsForTeam(cfg, state.team), size: cfg.sizes, background: cfg.backgrounds, frame: cfg.frames, paint: cfg.paints }[k];
        var item = byId(list, state[k]);
        if (c) c.textContent = item ? '— ' + item.label : '';
      });
      var fatal = hasFatalErrors(cfg);
      if (fatal) ref('stage').innerHTML = '<p class="mpc__status mpc__status--warn" role="alert">This plaque can\'t be previewed or ordered right now.</p>';
      else ref('stage').innerHTML = buildSvg(cfg, state, uid);
      var size = byId(cfg.sizes, state.size);
      ref('dims').textContent = size ? size.label + (size.approved === true ? '' : ' (demo size)') : 'No size selected';
      var props = orderProperties(cfg, state);
      ref('summary').innerHTML = Object.keys(props).filter(function (k) { return k.charAt(0) !== '_'; })
        .map(function (k) { return '<div class="mpc__row"><dt>' + esc(k) + '</dt><dd>' + esc(props[k]) + '</dd></div>'; }).join('') ||
        '<div class="mpc__row"><dt>Nothing selected</dt><dd></dd></div>';

      var res = current();
      var price = ref('price');
      var btn = el.querySelector('[data-action="add"], [data-action="simulate"]');
      var missingLabel = res.missing && res.missing.length ? 'Choose ' + res.missing.map(function (m) { return LABELS[m].toLowerCase(); }).join(', ') + '.' : '';
      if (res.status === 'invalid-config') {
        price.textContent = 'Not available to order right now.';
        if (btn) btn.disabled = true;
      } else if (mode === 'shopify' && ctx) {
        if (res.status === 'ready') price.textContent = res.variant.price_formatted || '';
        else if (res.status === 'unavailable') price.textContent = 'Currently unavailable';
        else if (res.status === 'unmapped') price.textContent = 'This combination is not available';
        else if (res.status === 'unapproved-size') price.textContent = 'This size is not available to order yet.';
        else if (res.status === 'missing-artwork') price.textContent = 'Artwork is not ready for this team.';
        else if (res.status === 'artwork-loading') price.textContent = 'Loading artwork…';
        else if (res.status === 'artwork-failed') price.textContent = 'Artwork could not be loaded. Please retry.';
        else price.textContent = missingLabel;
        btn.disabled = res.status !== 'ready' || submitter.isBusy();
      } else if (mode === 'demo') {
        var s = res.status === 'ready' ? 'Resolves to “' + (res.variant.title || res.variant.id) + '” ' + (res.variant.price_formatted || '') + ' · verify this is the intended variant' : res.status === 'unavailable' ? 'Fixture variant unavailable' : res.status === 'unmapped' ? 'No fixture mapping for this combination' : missingLabel;
        price.textContent = 'Demo fixtures — not real prices. ' + s;
      } else if (mode === 'shopify' || mode === 'shopify-empty') {
        if (btn) btn.disabled = true;
      }
      if (artworkLoader) ref('artwork-recover').hidden = res.status !== 'artwork-failed';
      syncForm(res);
      if (typeof options.onChange === 'function') options.onChange({ state: Object.assign({}, state), resolution: res, properties: props });
      return res;
    }

    function doAdd() {
      var res = update();
      if (res.status !== 'ready') { setStatus('Please complete your selection before adding to cart.', 'warn'); return; }
      if (!fetchImpl) { submitNative(); return; }
      var btn = el.querySelector('[data-action="add"]');
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      ref('recover').hidden = true;
      setStatus('Adding to cart…');
      submitter.submit({ variantId: res.variant.id, properties: orderProperties(cfg, state) }).then(function (r) {
        if (destroyed || r.status === 'busy') return;
        btn.removeAttribute('aria-busy');
        if (r.status === 'ok') {
          lastError = null;
          setStatus('Added to cart.', 'ok');
          var evt;
          try { evt = new CustomEvent('magnuson:cart-added', { bubbles: true, cancelable: true, detail: { item: r.body } }); } catch (e) { evt = null; }
          var proceed = evt ? el.dispatchEvent(evt) : true;
          if (proceed && options.afterAdd !== 'event' && typeof window !== 'undefined') window.location.href = routesRoot(options.routesRoot) + 'cart';
        } else {
          lastError = r.message;
          setStatus(r.message, 'error');
          ref('recover').hidden = false;
        }
        update();
      });
    }

    function submitNative() {
      var res = update();
      if (!form || res.status !== 'ready') return;
      form.submit();
    }

    function onChange(e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('.mpc-opt__input')) return;
      var key = t.getAttribute('data-key');
      state = setChoice(cfg, state, key, t.value);
      if (key === 'sport') renderTeams();
      setStatus('');
      ref('recover').hidden = true;
      update();
    }

    function onClick(e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
      if (!b || !el.contains(b)) return;
      var a = b.getAttribute('data-action');
      if (a === 'reset') {
        state = initialState(cfg); renderTeams(); setStatus('Selections reset.'); ref('recover').hidden = true; update();
      } else if (a === 'summary') {
        var miss = missingChoices(cfg, state);
        var text = summaryText(cfg, state);
        if (miss.length) { setStatus('Choose ' + miss.map(function (m) { return LABELS[m].toLowerCase(); }).join(', ') + ' to complete the summary.', 'warn'); return; }
        var done = function () { setStatus('Summary copied. Preview only — nothing was purchased.', 'ok'); };
        if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, function () { setStatus(text, 'ok'); });
        else setStatus(text, 'ok');
      } else if (a === 'simulate') {
        var r = update();
        var msg = { ready: 'Demo: fixture variant ' + (r.variant && r.variant.id) + ' would be used. Nothing is purchased in demo mode.', unavailable: 'Demo: mapped fixture variant is unavailable — Add to cart would be disabled.', 'invalid-config': 'Demo: configuration has fatal errors — preview and purchase are blocked.', unmapped: 'Demo: no variant mapping — Add to cart would be disabled.', incomplete: 'Demo: selection incomplete — Add to cart would be disabled.' }[r.status];
        setStatus(msg, r.status === 'ready' ? 'ok' : 'warn');
      } else if (a === 'artwork-retry' && artworkLoader) {
        artworkLoader.retry(); update();
      } else if (a === 'add' || a === 'retry') {
        doAdd();
      } else if (a === 'fallback') {
        submitNative();
      }
    }

    function onFormSubmit(e) {
      // Guard any theme-triggered submit of the hidden product form.
      if (current().status !== 'ready') e.preventDefault();
    }

    el.addEventListener('change', onChange);
    el.addEventListener('click', onClick);
    if (form) form.addEventListener('submit', onFormSubmit);
    renderTeams();
    update();

    var api = {
      uid: uid,
      config: cfg,
      getState: function () { return Object.assign({}, state); },
      setChoice: function (k, v) { state = setChoice(cfg, state, k, v); if (k === 'sport') renderTeams(); return update(); },
      reset: function () { state = initialState(cfg); renderTeams(); return update(); },
      lastError: function () { return lastError; },
      destroy: function () {
        destroyed = true;
        if (artworkLoader) artworkLoader.destroy();
        el.removeEventListener('change', onChange);
        el.removeEventListener('click', onClick);
        if (form) form.removeEventListener('submit', onFormSubmit);
        el.innerHTML = '';
        el.classList.remove('mpc-root');
        delete el.__magnusonPlaque;
      }
    };
    el.__magnusonPlaque = api;
    return api;
  }

  /* ---------------- auto-init (Shopify + standalone) ---------------- */
  function readJson(scope, sel) {
    var node = scope.querySelector(sel);
    if (!node) return null;
    try { return JSON.parse(node.textContent || 'null'); } catch (e) { return { __parseError: e.message }; }
  }

  function initRoot(host) {
    if (host.__magnusonPlaque) return host.__magnusonPlaque;
    if (host.__mpcLoading) return null;
    host.__mpcCancelled = false;
    var cfg = readJson(host, 'script[data-mpc-config]');
    var mountEl = host.querySelector('[data-mpc-mount]') || host;
    if (!cfg || cfg.__parseError) {
      mountEl.innerHTML = '<p class="mpc__error">Plaque configurator: configuration JSON could not be read' + (cfg && cfg.__parseError ? ' (' + esc(cfg.__parseError) + ')' : '') + '.</p>';
      return null;
    }
    ['walnut', 'white-oak'].forEach(function (id) {
      var img = host.getAttribute('data-mpc-image-' + id);
      var f = byId(cfg.frames || [], id);
      if (img && f) f.image = img;
    });
    var catalog = readJson(host, 'script[data-mpc-catalog]');
    if (catalog && catalog.__parseError) { mountEl.innerHTML = '<p class="mpc__error">Plaque configurator: team list could not be read.</p>'; return null; }
    if (catalog && catalog.records) {
      host.__mpcLoading = true;
      loadCatalogPages(host, catalog).then(function (records) {
        host.__mpcLoading = false;
        if (host.__mpcCancelled || !host.isConnected) return;
        finishInit(host, mountEl, applyCatalog(cfg, catalogFromRecords(records, { allowedSports: catalog.allowedSports })));
      }, function () {
        host.__mpcLoading = false;
        mountEl.innerHTML = '<p class="mpc__error">The full team list could not be loaded, so ordering is paused. Please refresh the page.</p>';
      });
      return null;
    }
    return finishInit(host, mountEl, cfg);
  }

  function loadCatalogPages(host, catalog) {
    var pages = Number(catalog.pages), currentPage = Number(catalog.page);
    if (pages === 0 && catalog.records && Array.isArray(catalog.records.teams) && !catalog.records.teams.length) pages = 1;
    if (!Number.isInteger(pages) || pages < 1 || pages > 40 || !Number.isInteger(currentPage) || currentPage < 1 || currentPage > pages) {
      return Promise.reject(new Error('Catalog pagination is invalid or exceeds the supported 40 pages.'));
    }
    if (pages === 1) return Promise.resolve(mergeRecordPages([catalog.records]));
    var sectionId = host.getAttribute('data-mpc-section');
    var pageParam = catalog.page_param;
    if (!sectionId || typeof pageParam !== 'string' || !pageParam) return Promise.reject(new Error('Catalog pagination context is missing.'));
    var baseUrl;
    try {
      baseUrl = new URL(window.location.href);
      baseUrl.hash = '';
      baseUrl.searchParams.delete('sections');
      baseUrl.searchParams.set('section_id', sectionId);
    } catch (e) { return Promise.reject(e); }
    var reqs = [];
    function requestPage(page) {
      var url = new URL(baseUrl.href);
      url.searchParams.set(pageParam, String(page));
      return Promise.resolve().then(function () { return fetch(url.href, { credentials: 'same-origin' }); })
        .then(function (res) { if (!res.ok) throw new Error('page ' + res.status); return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('script[data-mpc-catalog]');
          var data = node ? JSON.parse(node.textContent) : null;
          if (!data || !data.records || Number(data.page) !== page || Number(data.pages) !== pages || data.page_param !== pageParam) throw new Error('Catalog page is missing or pagination changed.');
          return data.records;
        });
    }
    for (var p = 1; p <= pages; p++) {
      reqs.push(p === currentPage ? Promise.resolve(catalog.records) : requestPage(p));
    }
    return Promise.all(reqs).then(mergeRecordPages);
  }

  function finishInit(host, mountEl, cfg) {
    var product = readJson(host, 'script[data-mpc-product]');
    var map = readJson(host, 'script[data-mpc-variant-map]') || [];
    var fixtures = readJson(host, 'script[data-mpc-demo-fixtures]');
    var mode = host.getAttribute('data-mpc-mode') || 'preview';
    var root = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var inst = mount(mountEl, {
      config: cfg, mode: mode, product: product, variantMap: map, fixtures: fixtures,
      form: host.querySelector('form[data-mpc-form]'), routesRoot: root,
      afterAdd: host.getAttribute('data-mpc-after-add') || 'cart',
      heading: host.hasAttribute('data-mpc-heading') ? host.getAttribute('data-mpc-heading') : null,
      eyebrow: host.hasAttribute('data-mpc-eyebrow') ? host.getAttribute('data-mpc-eyebrow') : null,
      buttonClass: host.getAttribute('data-mpc-button-class') || '',
      buttonSecondaryClass: host.getAttribute('data-mpc-button-secondary-class') || ''
    });
    host.__magnusonPlaque = inst;
    var origDestroy = inst.destroy;
    inst.destroy = function () { origDestroy(); delete host.__magnusonPlaque; };
    return inst;
  }

  function initAll(scope) {
    scope = scope || document;
    var list = [];
    if (scope.matches && scope.matches('[data-mpc-root]')) list.push(scope);
    Array.prototype.push.apply(list, scope.querySelectorAll ? scope.querySelectorAll('[data-mpc-root]') : []);
    return list.map(initRoot);
  }

  function destroyAll(scope) {
    scope = scope || document;
    var list = [];
    if (scope.matches && scope.matches('[data-mpc-root]')) list.push(scope);
    Array.prototype.push.apply(list, scope.querySelectorAll ? scope.querySelectorAll('[data-mpc-root]') : []);
    list.forEach(function (h) { if (h.__mpcLoading) h.__mpcCancelled = true; if (h.__magnusonPlaque) h.__magnusonPlaque.destroy(); });
  }

  var autoInitDone = false;
  function autoInit() {
    if (autoInitDone) return;
    autoInitDone = true;
    var go = function () { initAll(document); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
    document.addEventListener('shopify:section:unload', function (e) { destroyAll(e.target); });
  }

  return {
    version: VERSION,
    normalizeConfig: normalizeConfig,
    catalogFromRecords: catalogFromRecords,
    applyCatalog: applyCatalog,
    mergeRecordPages: mergeRecordPages,
    loadCatalogPages: loadCatalogPages,
    teamsForSport: teamsForSport,
    initialState: initialState,
    setChoice: setChoice,
    revalidate: revalidate,
    missingChoices: missingChoices,
    fitContain: fitContain,
    geometry: geometry,
    resolveVariant: resolveVariant,
    resolvePurchase: resolvePurchase,
    createArtworkLoader: createArtworkLoader,
    hasFatalErrors: hasFatalErrors,
    jsonForHtml: jsonForHtml,
    orderProperties: orderProperties,
    summaryText: summaryText,
    createCartSubmitter: createCartSubmitter,
    buildSvg: buildSvg,
    mount: mount,
    initAll: initAll,
    destroyAll: destroyAll,
    autoInit: autoInit
  };
});

